import { IonHeader, IonIcon, IonPage, IonTitle } from "@ionic/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { Toolbar } from "@/components/toolbar";
import { useAppearance } from "@/lib/hooks/use-appearance";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { checkIcon } from "@/lib/icons/check.icon";
import { useTheme } from "@/lib/providers/theme/context";
import { Routes } from "@/lib/router";
import { type Appearance } from "@/lib/store/preferences";
import { cx } from "@/lib/style/cva.config";
import { deriveDarkTheme } from "@/lib/theme/dark";
import { withReadableTones } from "@/lib/theme/readable";
import { type Theme } from "@/lib/theme/type";
import { convertThemeToCssVars } from "@/lib/theme/utils";

const OPTIONS = ["system", "light", "dark"] as const satisfies readonly Appearance[];

export function SettingsAppearancePage() {
  const { t } = useTranslation();
  const PreferredHaptics = usePreferredHaptics();
  const { brandTheme } = useTheme();
  const { preference, system, setPreference } = useAppearance();

  /**
   * Both appearances at once, as CSS variables scoped to a single element.
   *
   * The whole app is painted from `--color-*` on the document, which means a subtree can be handed
   * a different set and simply be another appearance — no duplicate styles, no screenshots to keep
   * up to date. That is what lets the picker show what the other option looks like without
   * switching into it first, in this organization's own colours.
   */
  const previews = useMemo(
    () => ({
      light: convertThemeToCssVars(withReadableTones(brandTheme)) as React.CSSProperties,
      dark: convertThemeToCssVars(
        withReadableTones(deriveDarkTheme(brandTheme)),
      ) as React.CSSProperties,
    }),
    [brandTheme],
  );

  function choose(appearance: Appearance) {
    if (appearance === preference) {
      return;
    }

    // The switch lands first and the feedback is fire-and-forget: Capacitor's web haptics throw
    // where `navigator.vibrate` does not exist — Safari, desktop, an iOS PWA — and a preference
    // that fails to save on those platforms would be a strange way to lose this.
    setPreference(appearance);
    PreferredHaptics.impact();
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar>
          <Buttons slot="secondary">
            <BackButton defaultHref={Routes.Settings()} />
          </Buttons>
          <IonTitle>{t("routes:appearance.title")}</IonTitle>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome>
        <div className="flex flex-1 flex-col gap-6 p-4">
          <div className="flex flex-col gap-2">
            <span className="w-fit rounded-full bg-surface-brand-primary px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-txt-brand">
              {t("settings.appearance.beta.badge")}
            </span>
            <p className="text-sm text-txt-secondary">{t("settings.appearance.intro")}</p>
          </div>

          <div
            role="radiogroup"
            aria-label={t("routes:appearance.title")}
            className="grid grid-cols-3 gap-3"
          >
            {OPTIONS.map((option) => (
              <AppearanceOption
                key={option}
                appearance={option}
                label={t(`settings.appearance.options.${option}`)}
                selected={preference === option}
                previews={previews}
                system={system}
                onSelect={() => choose(option)}
              />
            ))}
          </div>

          {preference === "system" && (
            <p className="text-sm text-txt-tertiary">
              {t("settings.appearance.following", {
                appearance: t(`settings.appearance.options.${system}`).toLowerCase(),
              })}
            </p>
          )}

          <p className="text-sm text-txt-tertiary">{t("settings.appearance.beta.note")}</p>
        </div>
      </Content>
    </IonPage>
  );
}

function AppearanceOption(props: {
  appearance: Appearance;
  label: string;
  selected: boolean;
  system: "light" | "dark";
  previews: { light: React.CSSProperties; dark: React.CSSProperties };
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      onClick={props.onSelect}
      className="flex flex-col items-center gap-2 text-center"
    >
      <span
        className={cx(
          "block w-full overflow-hidden rounded-xl border-2 transition-colors",
          props.selected ? "border-bordercolor-brand" : "border-bordercolor-primary",
        )}
      >
        {/*
          "Systeem" is not a third look — it is whichever of the other two the phone is on. Showing
          both halves says that, where a single swatch would leave the employee to guess which one
          they are about to get.
        */}
        {props.appearance === "system" ? (
          <span className="flex">
            <MiniatureApp vars={props.previews.light} className="w-1/2" />
            <MiniatureApp vars={props.previews.dark} className="w-1/2" />
          </span>
        ) : (
          <MiniatureApp vars={props.previews[props.appearance]} />
        )}
      </span>

      <span className="flex items-center gap-1">
        {props.selected && (
          <IonIcon icon={checkIcon} aria-hidden className="size-4 text-txt-brand" />
        )}
        <span
          className={cx(
            "text-sm",
            props.selected ? "font-semibold text-txt-primary" : "text-txt-secondary",
          )}
        >
          {props.label}
        </span>
      </span>
    </button>
  );
}

/**
 * A shift list, small enough to read as a picture of the app rather than as a screen.
 *
 * Every colour here comes from the same tokens the real screens use, so this cannot drift away from
 * what tapping the option actually does — if a brand's night changes, this changes with it.
 */
function MiniatureApp(props: { vars: React.CSSProperties; className?: string }) {
  return (
    <span
      style={props.vars}
      data-testid="appearance-preview"
      className={cx(
        "flex aspect-[3/4] flex-col gap-1.5 bg-surface-background p-2",
        props.className,
      )}
    >
      <span className="flex items-center gap-1">
        <span className="size-3 rounded-full bg-surface-brand-secondary" />
        <span className="h-1.5 w-6 rounded-full bg-txt-secondary" />
      </span>

      {[0, 1].map((row) => (
        <span key={row} className="flex flex-col gap-1 rounded bg-surface-primary p-1.5">
          <span className="h-1 w-full rounded-full bg-txt-secondary" />
          <span className="h-1 w-2/3 rounded-full bg-txt-tertiary" />
        </span>
      ))}

      <span className="mt-auto h-2.5 w-full rounded bg-button-primary" />
    </span>
  );
}
